import React, { useId } from "react";

const WEEKDAYS = [
  { value: 0, label: "Domingo", short: "Dom" },
  { value: 1, label: "Segunda", short: "Seg" },
  { value: 2, label: "Terça", short: "Ter" },
  { value: 3, label: "Quarta", short: "Qua" },
  { value: 4, label: "Quinta", short: "Qui" },
  { value: 5, label: "Sexta", short: "Sex" },
  { value: 6, label: "Sábado", short: "Sáb" },
];

export default function ScheduleConfigurator({ value, onChange, allowDisable = true }) {
  const state = value || {};
  const radioName = useId();
  const enabled = allowDisable ? !!state.enabled : true;

  const update = (partial) => {
    const next = { ...state, ...partial };
    if (!allowDisable) {
      next.enabled = true;
    }
    onChange(next);
  };

  const toggleWeekday = (weekday) => {
    const set = new Set(state.weekdays || []);
    if (set.has(weekday)) {
      set.delete(weekday);
    } else {
      set.add(weekday);
    }
    update({ weekdays: Array.from(set).sort((a, b) => a - b) });
  };

  const setTimeValue = (index, newValue) => {
    const times = Array.isArray(state.times) ? [...state.times] : [];
    times[index] = newValue;
    update({ times });
  };

  const addTime = () => {
    const times = Array.isArray(state.times) ? [...state.times] : [];
    times.push("08:00");
    update({ times });
  };

  const removeTime = (index) => {
    const times = Array.isArray(state.times) ? [...state.times] : [];
    times.splice(index, 1);
    update({ times });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-slate-700">
            Automatizar nova solicitação
          </p>
          <p className="text-xs text-slate-500">
            Agende a criação automática de instâncias para este processo.
          </p>
        </div>
        {allowDisable && (
          <label className="inline-flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => update({ enabled: event.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            Ativar
          </label>
        )}
      </div>

      {enabled ? (
        <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="radio"
                name={`schedule-mode-${radioName}`}
                value="once"
                checked={state.mode === "once"}
                onChange={() => update({ mode: "once" })}
                className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              Execução única
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="radio"
                name={`schedule-mode-${radioName}`}
                value="recurring"
                checked={state.mode === "recurring"}
                onChange={() => update({ mode: "recurring" })}
                className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              Repetir por semana
            </label>
          </div>

          {state.mode === "once" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Data
                </label>
                <input
                  type="date"
                  value={state.date || ""}
                  onChange={(event) => update({ date: event.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Horário
                </label>
                <input
                  type="time"
                  value={state.time || ""}
                  onChange={(event) => update({ time: event.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-200"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-slate-700">Dias da semana</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {WEEKDAYS.map((weekday) => {
                    const active = (state.weekdays || []).includes(weekday.value);
                    return (
                      <button
                        key={weekday.value}
                        type="button"
                        onClick={() => toggleWeekday(weekday.value)}
                        className={`rounded-full px-3 py-1 text-sm transition ${
                          active
                            ? "bg-blue-600 text-white shadow"
                            : "border border-slate-300 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-600"
                        }`}
                      >
                        {weekday.short}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">Horários</p>
                  <button
                    type="button"
                    onClick={addTime}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    Adicionar horário
                  </button>
                </div>
                <div className="space-y-2">
                  {(state.times || []).map((time, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="time"
                        value={time || ""}
                        onChange={(event) => setTimeValue(index, event.target.value)}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeTime(index)}
                        className="rounded-lg border border-transparent px-2 py-1 text-xs font-medium text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
                      >
                        Remover
                      </button>
                    </div>
                  ))}
                  {(!state.times || state.times.length === 0) && (
                    <p className="text-xs text-slate-500">
                      Nenhum horário selecionado. Adicione pelo menos um horário para continuar.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

